<template>
    <!-- Desktop Modal -->
    <UiModal
        v-if="!isMobile"
        :is-open="modelValue"
        @close="close"
        width="560px"
        :no-padding="true"
        modal-class="overflow-hidden"
    >
        <template #title>
            <span></span>
        </template>

        <template #close>
            <span></span>
        </template>

        <template #content>
            <div>
                <!-- Image with background and gradient overlay -->
                <div class="relative bg-slate-100 dark:bg-slate-700 overflow-hidden px-6 pt-4">
                    <!-- Close button -->
                    <div class="flex justify-end">
                        <button
                            type="button"
                            class="w-10 h-10 flex items-center justify-center rounded-lg focus:outline-none focus:ring focus:ring-inset focus:ring-slate-200"
                            @click="close"
                        >
                            <Icon
                                icon="x"
                                path-class="stroke-slate-500 hover:stroke-slate-700"
                                svg-class="fill-slate-500 hover:fill-slate-700 h-5 w-5"
                            />
                        </button>
                    </div>
                    <img
                        :src="importRedemptionsImage"
                        alt="Import redemptions preview"
                        class="w-full"
                    />
                    <div
                        class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-100 to-transparent pointer-events-none"
                    ></div>
                </div>

                <!-- Content -->
                <div class="mt-6 px-6">
                    <h2 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
                        Import redemptions from email
                    </h2>

                    <div
                        class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 mt-4 mb-2"
                    >
                        Forward your redemption confirmation emails to:
                    </div>

                    <div
                        class="flex items-center justify-between w-full bg-slate-50 dark:bg-slate-900/40 rounded-lg px-4 py-3 mb-6"
                    >
                        <span
                            class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 flex-1"
                        >
                            redemptions@10xtravel.com
                        </span>
                        <Tooltip yPlacement="top" xPlacement="center">
                            <template #trigger>
                                <div
                                    @click="copyEmail"
                                    class="text-slate-400 hover:text-slate-600 dark:text-slate-300 p-1 cursor-pointer"
                                >
                                    <Icon icon="copy" :width="20" :height="20" />
                                </div>
                            </template>
                            <span class="flex items-center gap-1 whitespace-nowrap">
                                <Icon
                                    v-if="isCopied"
                                    icon="check"
                                    :width="14"
                                    :height="14"
                                    class="text-white"
                                />
                                {{ copyTooltipText }}
                            </span>
                        </Tooltip>
                    </div>

                    <div class="flex flex-col gap-4 w-full mb-6">
                        <div class="flex items-center gap-3">
                            <span
                                class="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0"
                            >
                                1
                            </span>
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 flex-1"
                            >
                                Open any booking confirmation email
                            </span>
                        </div>
                        <div class="flex items-center gap-3">
                            <span
                                class="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0"
                            >
                                2
                            </span>
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 flex-1"
                            >
                                Forward to the above email address from your
                                {{
                                    allSenderEmails.length > 1
                                        ? 'emails'
                                        : 'email'
                                }}: {{ allSenderEmails.join(', ') }}
                            </span>
                        </div>
                        <div class="flex items-center gap-3">
                            <span
                                class="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0"
                            >
                                3
                            </span>
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 flex-1"
                            >
                                Redemption will automatically be created and
                                added to your trip
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div
                class="flex flex-col sm:flex-row items-center justify-between gap-3 w-full px-6 pb-4"
            >
                <a
                    href="/settings/email-import"
                    @click.prevent="navigateToManageEmails"
                    class="flex items-center gap-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:text-slate-300 cursor-pointer rounded"
                >
                    <Icon icon="settings" :width="20" :height="20" />
                    Manage sender emails
                </a>
                <button
                    @click="close"
                    class="flex items-center justify-center px-4 py-2 rounded-full bg-[#FD421D] shadow-sm text-sm font-medium leading-5 text-white hover:bg-[#e53b19]"
                >
                    Got it
                </button>
            </div>
        </template>
    </UiModal>

    <!-- Mobile Modal -->
    <UiModalMobile
        v-else
        :is-open="modelValue"
        title=""
        @close="close"
    >
        <template #header>
            <div class="flex items-center justify-end w-full">
                <button
                    @click="close"
                    class="btn-base btn-icon !m-0"
                    type="button"
                >
                    <Icon icon="x" class="text-slate-500 dark:text-slate-400" />
                </button>
            </div>
        </template>

        <template #content>
            <div>
                <!-- Image with background and gradient overlay -->
                <div class="relative bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                    <img
                        :src="importRedemptionsImage"
                        alt="Import redemptions preview"
                        class="w-full"
                    />
                    <div
                        class="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-100 to-transparent pointer-events-none"
                    ></div>
                </div>

                <!-- Content -->
                <div class="mt-6">
                    <h2 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
                        Import redemptions from email
                    </h2>

                    <div
                        class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 mt-4 mb-2"
                    >
                        Forward your redemption confirmation emails to:
                    </div>

                    <div
                        class="flex items-center justify-between w-full bg-slate-50 dark:bg-slate-900/40 rounded-lg px-4 py-3 mb-6"
                    >
                        <span
                            class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 flex-1"
                        >
                            redemptions@10xtravel.com
                        </span>
                        <Tooltip yPlacement="top" xPlacement="center">
                            <template #trigger>
                                <div
                                    @click="copyEmail"
                                    class="text-slate-400 hover:text-slate-600 dark:text-slate-300 p-1 cursor-pointer"
                                >
                                    <Icon icon="copy" :width="20" :height="20" />
                                </div>
                            </template>
                            <span class="flex items-center gap-1 whitespace-nowrap">
                                <Icon
                                    v-if="isCopied"
                                    icon="check"
                                    :width="14"
                                    :height="14"
                                    class="text-white"
                                />
                                {{ copyTooltipText }}
                            </span>
                        </Tooltip>
                    </div>

                    <div class="flex flex-col gap-4 w-full mb-6">
                        <div class="flex items-center gap-3">
                            <span
                                class="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0"
                            >
                                1
                            </span>
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 flex-1"
                            >
                                Open any booking confirmation email
                            </span>
                        </div>
                        <div class="flex items-center gap-3">
                            <span
                                class="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0"
                            >
                                2
                            </span>
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 flex-1"
                            >
                                Forward to the above email address from your
                                {{
                                    allSenderEmails.length > 1
                                        ? 'emails'
                                        : 'email'
                                }}: {{ allSenderEmails.join(', ') }}
                            </span>
                        </div>
                        <div class="flex items-center gap-3">
                            <span
                                class="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-sm font-medium text-slate-500 dark:text-slate-400 shrink-0"
                            >
                                3
                            </span>
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 flex-1"
                            >
                                Redemption will automatically be created and
                                added to your trip
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div
                class="flex flex-col sm:flex-row items-center justify-between gap-3 w-full"
            >
                <a
                    href="/settings/email-import"
                    @click.prevent="navigateToManageEmails"
                    class="flex items-center gap-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:text-slate-300 cursor-pointer rounded"
                >
                    <Icon icon="settings" :width="20" :height="20" />
                    Manage sender emails
                </a>
                <button
                    @click="close"
                    class="flex items-center justify-center px-4 py-2 rounded-full bg-[#FD421D] shadow-sm text-sm font-medium leading-5 text-white hover:bg-[#e53b19]"
                >
                    Got it
                </button>
            </div>
        </template>
    </UiModalMobile>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import Icon from '@/components/ui/icon/icon.vue';
import Tooltip from '@/components/tooltip.vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import { useIsMobile } from '@/composables/useIsMobile';
import { Apii } from '@/services/api';
import importRedemptionsImage from '../../../../images/import-redemptions-learn-more-modal.png';

const { isMobile } = useIsMobile();

const ApiService = new Apii();

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);

const page = usePage();
const userEmail = computed(
    () => page.props.userData?.email || 'your email'
);

const forwardingEmails = ref([]);
const copyTooltipText = ref('Copy email');
const isCopied = ref(false);
let copyTooltipTimer = null;

const allSenderEmails = computed(() => {
    const emails = [userEmail.value];
    forwardingEmails.value.forEach((e) => {
        if (e.email && e.email !== userEmail.value) emails.push(e.email);
    });
    return emails;
});

const fetchForwardingEmails = async () => {
    try {
        const response = await ApiService.apiCall(
            '/user/forwarding-emails',
            'get'
        );
        forwardingEmails.value = (response.data?.forwarding_emails || []).filter(
            (e) => e.verified
        );
    } catch {
        forwardingEmails.value = [];
    }
};

watch(
    () => props.modelValue,
    (val) => {
        if (val) fetchForwardingEmails();
    },
    { immediate: true }
);

const copyEmail = async () => {
    try {
        await navigator.clipboard.writeText('redemptions@10xtravel.com');
        copyTooltipText.value = 'Copied';
        isCopied.value = true;
        if (copyTooltipTimer) clearTimeout(copyTooltipTimer);
        copyTooltipTimer = setTimeout(() => {
            copyTooltipText.value = 'Copy email';
            isCopied.value = false;
        }, 2000);
    } catch {
        // Fallback silently
    }
};

const close = () => {
    emit('update:modelValue', false);
};

const navigateToManageEmails = () => {
    close();
    router.visit('/settings/email-import');
};
</script>
